import { Injectable } from '@angular/core';
import { SignInService } from './sign-in/sign-in.service';
import { IApisForWindows, IAuthData, IOrgLoginResponse, ISessionType, IState } from './sdk-tester.types';
import kcSdkConfig from '../assets/sdk/kc-sdk-config.json';
import { StringService } from './string.service';
import { first, cloneDeep } from 'lodash-es';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class SdkTesterService {
    kcSdk = (window as any).kcSdk;
    sessionTypes: ISessionType[] = [
        this.kcSdk.sessionTypes.DX,
        this.kcSdk.sessionTypes.CMPX,
        this.kcSdk.sessionTypes.I10CM,
        this.kcSdk.sessionTypes.ICD10PCS,
        this.kcSdk.sessionTypes.HCPCS
    ];
    i9I10States: IState[] = [this.kcSdk.i9I10States.I9, this.kcSdk.i9I10States.I10];
    patientTypes = [
        this.kcSdk.patientType.INPATIENT,
        this.kcSdk.patientType.OUTPATIENT,
        this.kcSdk.patientType.IRF,
        this.kcSdk.patientType.CAH,
        this.kcSdk.patientType.SNF
    ];
    selectedPatientType = this.patientTypes[0];
    selectedI9I10State = this.i9I10States[1];
    selectedSessionType: ISessionType = this.sessionTypes[0];
    selectedEncoderTab = this.kcSdk.encoderTabs.LOGIC;
    encoderPhraseCode = '';
    WEB_KC = 'web/KC.html';
    WEB_ENCODER = 'web/Encoder.html';
    exampleUrl = 'http://www.google.com';

    exampleDemographicsData = {
        sex: 'M',
        sendingFacility: '001',
        birthDate: this.stringService.getBirthDateISO(),
        admitDate: this.stringService.getCurrentDateISO(),
        dischargeDate: this.stringService.getCurrentDateISO(),
        patientType: this.kcSdk.patientType.INPATIENT,
        financialClass: '09'
    };

    encoderDemographics = JSON.stringify(this.exampleDemographicsData, null, 2);
    encoderTabs = [this.kcSdk.encoderTabs.LOGIC, this.kcSdk.encoderTabs.INDEX, this.kcSdk.encoderTabs.TABULAR];

    clientExampleToken = '';
    selectedPod = '';
    outputJson = '';
    sdkKcIsListening = false;
    sdkEncoderIsListening = false;

    version = this.kcSdk.version;

    exampleAuthToken = {
        access_token: ''
    };

    closeAllKCWindows = this.kcSdk.closeAllKCWindows;
    workflow = this.kcSdk.workflow;
    selectedWorkflowApi = first(this.kcSdk.workflow);
    workflowInput: string | undefined = kcSdkConfig.url + this.WEB_KC;
    kcWindowName = this.kcSdk.kcWindow.name;
    encoderWindowName = this.kcSdk.encoderWindow.name;
    apisForAllWindows: IApisForWindows[] = [
        {
            name: 'launch KC',
            fn: this.kcSdk.kcWindow.launch,
            windowName: this.kcWindowName
        },
        {
            name: 'launch Encoder',
            fn: this.kcSdk.encoderWindow.launch,
            windowName: this.encoderWindowName
        },
        {
            name: 'loadPatientRecord',
            fn: this.kcSdk.kcWindow.loadPatientRecord,
            windowName: this.kcWindowName
        },
        {
            name: 'assignConfig',
            fn: this.kcSdk.kcWindow.assignConfig,
            windowName: this.kcWindowName
        },
        {
            name: 'setAuthToken',
            fn: this.kcSdk.kcWindow.setAuthToken,
            windowName: this.kcWindowName
        },
        {
            name: 'setDemographics',
            fn: this.kcSdk.kcWindow.setDemographics,
            windowName: this.kcWindowName
        },
        {
            name: 'setAuthToken',
            fn: this.kcSdk.encoderWindow.setAuthToken,
            windowName: this.encoderWindowName
        },
        {
            name: 'setDemographics',
            fn: this.kcSdk.encoderWindow.setDemographics,
            windowName: this.encoderWindowName
        },
        {
            name: 'addCodes',
            fn: this.kcSdk.kcWindow.addCodes,
            windowName: this.kcWindowName
        },
        {
            name: 'clearPatientRecord',
            fn: this.kcSdk.kcWindow.clearPatientRecord,
            windowName: this.kcWindowName
        },
        {
            name: 'clearSelectedRow',
            fn: this.kcSdk.kcWindow.clearSelectedRow,
            windowName: this.kcWindowName
        },
        {
            name: 'closeAllKCWindowsExceptEncoder',
            fn: this.kcSdk.kcWindow.closeAllKCWindowsExceptEncoder,
            windowName: this.kcWindowName
        },
        {
            name: 'deleteCodes',
            fn: this.kcSdk.kcWindow.deleteCodes,
            windowName: this.kcWindowName
        },
        {
            name: 'getPayerList',
            fn: this.kcSdk.kcWindow.getPayerList,
            windowName: this.kcWindowName
        },
        {
            name: 'getRowSpecificInfo',
            fn: this.kcSdk.kcWindow.getRowSpecificInfo,
            windowName: this.kcWindowName
        },
        {
            name: 'hasCriticalEdits',
            fn: this.kcSdk.kcWindow.hasCriticalEdits,
            windowName: this.kcWindowName
        },
        {
            name: 'hasEdits',
            fn: this.kcSdk.kcWindow.hasEdits,
            windowName: this.kcWindowName
        },
        {
            name: 'launchCodeSummaryReport',
            fn: this.kcSdk.kcWindow.launchCodeSummaryReport,
            windowName: this.kcWindowName
        },
        {
            name: 'launchEdits',
            fn: this.kcSdk.kcWindow.launchEdits,
            windowName: this.kcWindowName
        },
        {
            name: 'launchIndex',
            fn: this.kcSdk.kcWindow.launchIndex,
            windowName: this.kcWindowName
        },
        {
            name: 'launchLogic',
            fn: this.kcSdk.kcWindow.launchLogic,
            windowName: this.kcWindowName
        },
        {
            name: 'launchTabular',
            fn: this.kcSdk.kcWindow.launchTabular,
            windowName: this.kcWindowName
        },
        {
            name: 'launchFacilityLookup',
            fn: this.kcSdk.kcWindow.launchFacilityLookup,
            windowName: this.kcWindowName
        },
        {
            name: 'launchIndex',
            fn: this.kcSdk.encoderWindow.launchIndex,
            windowName: this.encoderWindowName
        },
        {
            name: 'launchLogic',
            fn: this.kcSdk.encoderWindow.launchLogic,
            windowName: this.encoderWindowName
        },
        {
            name: 'launchTabular',
            fn: this.kcSdk.encoderWindow.launchTabular,
            windowName: this.encoderWindowName
        },
        {
            name: 'logout',
            fn: this.kcSdk.kcWindow.logout,
            windowName: this.kcWindowName
        },
        {
            name: 'resetApplication',
            fn: this.kcSdk.kcWindow.resetApplication,
            windowName: this.kcWindowName
        },
        {
            name: 'savePatientRecord',
            fn: this.kcSdk.kcWindow.savePatientRecord,
            windowName: this.kcWindowName
        },
        {
            name: 'updateCodes',
            fn: this.kcSdk.kcWindow.updateCodes,
            windowName: this.kcWindowName
        },
        {
            name: 'updateDemographics',
            fn: this.kcSdk.kcWindow.updateDemographics,
            windowName: this.kcWindowName
        },
        {
            name: 'openEncoder',
            fn: this.kcSdk.kcWindow.openEncoder,
            windowName: this.kcWindowName
        },
    ];

    availableApis = cloneDeep(this.apisForAllWindows);
    availableApiSelected: IApisForWindows = this.availableApis[0];
    workflowWindow = this.kcWindowName;
    runWorkflow = this.kcSdk.processWorkflow;

    constructor(private signInService: SignInService, private stringService: StringService, private http: HttpClient) {
        this.selectedSessionType = this.sessionTypes[0];
    }

    doSigninSubmit = () => {
        setTimeout(() => {
            if (!this.signInService.signInForm.valid) {
                this.signInService.serverError.visible = false;
                this.signInService.clientError.visible = true;
                this.signInService.signInForm.markAsPristine();

                return;
            }

            const advancedMode = document.getElementById('kc-sdk-advanced-mode') as HTMLInputElement;

            if (advancedMode.checked) {
                this.generateToken();
            } else {
                const authData = {
                    grant_type: 'org_login',
                    username: this.signInService.signInForm.controls['username'].value,
                    organizationId: this.signInService.signInForm.controls['organization'].value,
                    enableEncoder: true,
                    client_id: kcSdkConfig.appName,
                    client_secret: kcSdkConfig.secret
                };

                this.setAuthToken(authData).subscribe({
                    next: () => {
                        this.launchEncoder();
                    },
                    error: (response: any) => {
                        this.signInService.serverError.content = response;
                    }
                });
            }
            this.signInService.clearClientError();
        });
    };

    generateToken = () => {
        if (this.signInService.signInForm.valid) {
            const authData = {
                grant_type: 'org_login',
                username: this.signInService.signInForm.controls['username'].value,
                organizationId: this.signInService.signInForm.controls['organization'].value,
                enableEncoder: true,
                client_id: kcSdkConfig.appName,
                client_secret: kcSdkConfig.secret
            };
            this.setAuthToken(authData).subscribe({
                next: () => {
                    this.signInService.clearClientError();
                    this.signInService.serverError.visible = false;
                    this.signInService.clientError.visible = false;
                    this.signInService.signInForm.markAsPristine();
                    this.clientExampleToken = this.exampleAuthToken.access_token;
                },
                error: (response: any) => {
                    this.signInService.serverError.content = response;
                }
            });
        } else {
            this.signInService.serverError.visible = false;
            this.signInService.clientError.content = 'Review the form and correct highlighted fields.';
            this.signInService.clientError.visible = true;
            this.signInService.signInForm.markAsPristine();
        }
    };

    launchEncoder = () => {
        const encoderRequest = {
            sessionType: this.selectedSessionType,
            encoderTab: this.selectedEncoderTab,
            codePhrase: this.encoderPhraseCode
        };

        const url = kcSdkConfig.url + this.WEB_ENCODER;
        const demographics = this.encoderDemographics;
        const state = this.selectedI9I10State;
        if (this.clientExampleToken) {
            this.exampleAuthToken.access_token = this.clientExampleToken;
        }

        this.kcSdk.encoderWindow.launch(url, this.exampleAuthToken, state, demographics, encoderRequest);
    };

    setAuthToken = (authData: IAuthData): Observable<any> => {
        return new Observable(obs => {
            const requestHeader = {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' })
            };
            const authOrgUser = {
                username: btoa(authData.username),
                organization: authData.organizationId,
                clientId: kcSdkConfig.appName
            };

            this.http.post<IOrgLoginResponse>(kcSdkConfig.tokenServer + 'orgLogin', authOrgUser, requestHeader).subscribe({
                next: (response: IOrgLoginResponse) => {
                    if (response) {
                        this.exampleAuthToken.access_token = response.token;
                        this.logOutput('auth', "token generated - userName:'" + authData.username + "' org: '" + authData.organizationId + "'");
                    }
                    obs.next(response);
                },
                error: (response: any) => {
                    if (response.status === 401) {
                        this.exampleAuthToken.access_token = '';

                        this.signInService.serverError.visible = false;
                        this.signInService.clientError.content = 'Failed to authenticate user.';
                        this.signInService.clientError.visible = true;
                        this.signInService.signInForm.markAsPristine();
                    }
                    const description = response && response.message ? response.message : 'undefined error message';
                    this.logOutput('auth', 'token exception: ' + description);
                    obs.error(description);
                }
            });
        });
    };

    onStateChange = () => {
        this.filterSessionTypes(this.selectedI9I10State);
    };

    filterSessionTypes(state: IState) {
        this.sessionTypes.length = 0;
        for (const session in this.kcSdk.sessionTypes) {
            const s = this.kcSdk.sessionTypes[session];
            if (s.state.indexOf(state.value) > -1) {
                this.sessionTypes.push(s);
            }
        }

        this.selectedSessionType = this.sessionTypes[0];
    }

    onPatientTypeChange = () => {
        try {
            const demo = JSON.parse(this.encoderDemographics);
            demo.patientType = this.selectedPatientType;
            this.encoderDemographics = JSON.stringify(demo, null, 2);
        } catch (e) {
            this.exampleDemographicsData.patientType = this.selectedPatientType;
            this.encoderDemographics = JSON.stringify(this.exampleDemographicsData, null, 2);
        }
    };

    logOutput(event: any, message: any) {
        const jsonOutput = {
            event: event,
            response: this.stringService.isJson(message) ? JSON.parse(message) : message
        };

        if (jsonOutput.event === 'editGroupPriceCompleted') {
            jsonOutput.response = [];
        }

        this.outputJson = this.outputJson
            ? [this.stringService.removeBrackets(this.outputJson), JSON.stringify(jsonOutput, null, 2)].join(',\n')
            : JSON.stringify(jsonOutput, null, 2);

        this.outputJson = this.stringService.wrapAsArray(this.outputJson);
        this.scrollLogToBottom();
    }

    scrollLogToBottom() {
        setTimeout(() => {
            const log = document.getElementById('kc-sdk-input');
            if (log) {
                log.scrollTop = log.scrollHeight;
            }
        });
    }

    launchKc = () => {
        if (!this.signInService.signInForm.valid) {
            this.signInService.serverError.visible = false;
            this.signInService.clientError.visible = true;
            this.signInService.signInForm.markAsPristine();

            return;
        }
        this.signInService.clearClientError();

        const authData = {
            grant_type: 'org_login',
            username: this.signInService.signInForm.controls['username'].value,
            organizationId: this.signInService.signInForm.controls['organization'].value,
            enableEncoder: true,
            client_id: kcSdkConfig.appName,
            client_secret: kcSdkConfig.secret
        };

        this.setAuthToken(authData).subscribe({
            next: response => {
                const url = kcSdkConfig.url + this.WEB_KC;
                const demographics = this.encoderDemographics;
                const state = this.selectedI9I10State;

                if (this.clientExampleToken) {
                    this.exampleAuthToken.access_token = this.clientExampleToken;
                }

                this.kcSdk.kcWindow.launch(url, this.exampleAuthToken, state, demographics);
            },
            error: (response: any) => {
                this.signInService.serverError.content = response;
            }
        });
    };

    launchReferences = () => {
        if (!this.signInService.signInForm.valid) {
            this.signInService.serverError.visible = false;
            this.signInService.clientError.visible = true;
            this.signInService.signInForm.markAsPristine();

            return;
        }
        this.signInService.clearClientError();
        const authData = {
            grant_type: 'org_login',
            username: this.signInService.signInForm.controls['username'].value,
            organizationId: this.signInService.signInForm.controls['organization'].value,
            enableEncoder: true,
            client_id: kcSdkConfig.appName,
            client_secret: kcSdkConfig.secret
        };

        this.setAuthToken(authData).subscribe({
            next: () => {
                this.kcSdk.launchReferences(this.encoderPhraseCode, this.selectedSessionType.name, this.exampleAuthToken.access_token);
            },
            error: (response: any) => {
                this.signInService.serverError.content = response;
            }
        });
    };
}
